import { useState, useRef, useEffect, useCallback } from "react";
import {
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { CheckIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { useClickOutside } from "hooks";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

export function MultiSelectFilterDropdown({
  filterKey,
  options = [],
  selectedValues = [],
  onSelect,
  onClear,
  label,
  placeholder = "Select Here",
  showAllOption = true,
  showSearch = true,
  searchThreshold = 0,
  className = "",
  buttonClassName = "",
  usePortal = false,
  portalTarget = null,
  // Controlled state props for COORDINATED closing
  isOpen: propsIsOpen,
  onToggle: propsOnToggle,
  isLoading = false,
  onOpen,
}) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const buttonRef = useRef(null);

  // Decide whether to use internal or external state
  const isControlled = propsIsOpen !== undefined && propsOnToggle !== undefined;
  const isOpen = isControlled ? propsIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? propsOnToggle : setInternalIsOpen;

  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, (e) => {
    // If click is inside portal, don't close
    if (usePortal && portalRef.current && portalRef.current.contains(e.target)) {
      return;
    }

    if (isOpen) {
      if (isControlled) {
        setIsOpen(null); // Signal parent to close everything
      } else {
        setInternalIsOpen(false);
      }
    }
  });

  const updatePosition = useCallback(() => {
    if (buttonRef.current && isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Open upwards if space below is less than 280px and space above is greater
      const preferUp = spaceBelow < 280 && spaceAbove > spaceBelow;
      const maxH = preferUp 
        ? Math.max(150, spaceAbove - 20) 
        : Math.max(150, spaceBelow - 20);

      const finalWidth = rect.width < 260 ? 260 : rect.width;
      let finalLeft = rect.left;
      if (finalLeft + finalWidth > window.innerWidth - 8) {
        finalLeft = Math.max(8, rect.right - finalWidth);
      }

      setDropdownRect({
        top: rect.bottom,
        rectTop: rect.top,
        left: finalLeft,
        width: rect.width,
        isUp: preferUp,
        maxHeight: maxH,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && usePortal) {
      updatePosition();
      requestAnimationFrame(updatePosition);
      window.addEventListener('resize', updatePosition);

      const handleScroll = (e) => {
        if (portalRef.current && portalRef.current.contains(e.target)) return;
        if (isControlled) {
          setIsOpen(null);
        } else {
          setInternalIsOpen(false);
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, usePortal, updatePosition, isControlled, setIsOpen]);

  const toggleDropdown = () => {
    const willOpen = isControlled ? !isOpen : !internalIsOpen;
    if (willOpen && typeof onOpen === "function") {
      onOpen();
    }
    if (isControlled) {
      setIsOpen(isOpen ? null : filterKey);
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const getFilteredOptions = (options, searchTerm) => {
    if (!searchTerm) return options || [];
    const term = searchTerm.toLowerCase();
    return (options || []).filter((option) =>
      option.label.toLowerCase().includes(term) ||
      (option.badgeText && option.badgeText.toLowerCase().includes(term)),
    );
  };

  const getFilterDisplayValue = (selectedValues, options) => {
    if (isLoading) return "Loading...";
    if (!selectedValues || selectedValues.length === 0) return placeholder;

    if (selectedValues.length === 1) {
      const option = options?.find((opt) => opt.id === selectedValues[0]);
      return option?.label || "";
    }

    return `${selectedValues.length} selected`;
  };

  const filteredOptions = getFilteredOptions(options, searchTerm);
  const shouldShowSearch =
    showSearch && options && options.length > searchThreshold;

  const handleSelect = (value) => {
    const currentValues = Array.isArray(selectedValues) ? [...selectedValues] : [];

    if (currentValues.includes(value)) {
      // Remove value if already selected
      const newValues = currentValues.filter(v => v !== value);
      onSelect?.(filterKey, newValues);
    } else {
      // Add value to selection
      const newValues = [...currentValues, value];
      onSelect?.(filterKey, newValues);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onClear?.(filterKey);
  };

  const isSelected = (value) => {
    return Array.isArray(selectedValues) && selectedValues.includes(value);
  };

  return (
    <div className={clsx("relative", className)} ref={dropdownRef}>
      {label && (
        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
          {label}
        </label>
      )}

      <div className="relative">
        <Button
          ref={buttonRef}
          variant="outlined"
          className={clsx(
            "flex w-full items-center justify-between gap-2 border-gray-300 bg-white px-3 py-2 text-xs transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700",
            buttonClassName,
            selectedValues &&
            selectedValues.length > 0 &&
            "border-primary-500 bg-primary-50 dark:bg-primary-900/20",
          )}
          onClick={toggleDropdown}
        >
          <span
            className={clsx(
              "truncate flex items-center gap-1.5",
              selectedValues && selectedValues.length > 0
                ? "text-primary-700 dark:text-primary-300 font-medium"
                : "text-gray-500 dark:text-gray-400",
            )}
          >
            {isLoading && (
              <svg className="animate-spin h-3.5 w-3.5 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {getFilterDisplayValue(selectedValues, options)}
          </span>
          <div className="flex items-center gap-1">
            {selectedValues && selectedValues.length > 0 && (
              <XCircleIcon
                className="size-4 text-gray-400 transition-colors hover:text-red-500"
                onClick={handleClear}
              />
            )}
            <ChevronDownIcon
              className={clsx(
                "size-4 transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </Button>

        {/* Dropdown Options */}
        {isOpen && (() => {
          const dropdownContent = (
            <div
              ref={portalRef}
              className={clsx(
                "z-[200000] mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800",
                usePortal ? "fixed" : "absolute min-w-full w-max max-w-[400px]"
              )}
              style={usePortal && dropdownRect ? {
                top: dropdownRect.isUp ? undefined : `${dropdownRect.top + 4}px`,
                bottom: dropdownRect.isUp ? `${window.innerHeight - dropdownRect.rectTop + 4}px` : undefined,
                left: `${dropdownRect.left}px`,
                minWidth: dropdownRect.width < 260 ? '260px' : `${dropdownRect.width}px`,
                width: 'max-content',
                maxWidth: '400px'
              } : {}}
            >
              {/* Search Input */}
              {shouldShowSearch && (
                <div className="border-b border-gray-200 p-3 dark:border-gray-600">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-550 dark:hover:text-dark-300 focus:outline-none cursor-pointer"
                      >
                        <XMarkIcon className="size-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Options List */}
              <div 
                className="overflow-y-auto custom-scrollbar"
                style={{
                  maxHeight: usePortal && dropdownRect?.maxHeight 
                    ? `${dropdownRect.maxHeight - 100}px` 
                    : '240px'
                }}
              >
                {showAllOption && (() => {
                  const isAllSelected = options.length > 0 && options.every(opt => isSelected(opt.value));
                  return (
                    <button
                      onClick={() => {
                        if (isAllSelected) {
                          onClear?.(filterKey);
                        } else {
                          const allValues = options.map((opt) => opt.value);
                          onSelect?.(filterKey, allValues);
                        }
                      }}
                      className={clsx(
                        "flex w-full items-center gap-3 px-3 py-2 text-left text-xs transition-colors",
                        "hover:bg-gray-100 dark:hover:bg-gray-700",
                        isAllSelected
                          ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                          : "text-gray-700 dark:text-gray-300"
                      )}
                    >
                      <div className="flex shrink-0 items-center">
                        <div
                          className={clsx(
                            "flex size-4 items-center justify-center rounded border",
                            isAllSelected
                              ? "border-primary-500 bg-primary-500"
                              : "border-gray-300 dark:border-gray-600"
                          )}
                        >
                          {isAllSelected && (
                            <CheckIcon className="size-3 text-white" />
                          )}
                        </div>
                      </div>
                      <span className="flex-1 truncate leading-relaxed">
                        All
                      </span>
                    </button>
                  );
                })()}

                {filteredOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(option.value)}
                    className={clsx(
                      "flex w-full items-start gap-3 px-3 py-2 text-left text-xs transition-colors",
                      "hover:bg-gray-100 dark:hover:bg-gray-700",
                      isSelected(option.value)
                        ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                        : "text-gray-700 dark:text-gray-300"
                    )}
                  >
                    <div className="flex shrink-0 items-center mt-0.5">
                      <div
                        className={clsx(
                          "flex size-4 items-center justify-center rounded border",
                          isSelected(option.value)
                            ? "border-primary-500 bg-primary-500"
                            : "border-gray-300 dark:border-gray-600"
                        )}
                      >
                        {isSelected(option.value) && (
                          <CheckIcon className="size-3 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className="leading-relaxed whitespace-normal break-words pr-2">
                        {typeof option.label === 'string' ? option.label.replace(/\//g, '/\u200B') : option.label}
                      </span>
                      {option.badgeText && (() => {
                        const badges = (typeof option.badgeText === 'string' 
                          ? option.badgeText.split(',') 
                          : (Array.isArray(option.badgeText) ? option.badgeText : [option.badgeText]))
                          .map(b => typeof b === 'string' ? b.trim() : b)
                          .filter(Boolean);
                        
                        if (badges.length === 0) return null;
                        
                        const maxVisible = 1;
                        const visibleBadges = badges.slice(0, maxVisible);
                        const extraBadges = badges.slice(maxVisible);
                        
                        return (
                          <div className="ml-2 flex shrink-0 items-center justify-end gap-1 flex-wrap">
                            {visibleBadges.map((badge, idx) => (
                              <span key={idx} title={badge} className="shrink-0 max-w-full inline-flex items-center rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                                <span className="truncate">{badge}</span>
                              </span>
                            ))}
                            {extraBadges.length > 0 && (
                              <div className="relative inline-flex items-center">
                                <span 
                                  data-tooltip-id={`badge-tooltip-filter-${option.value || option.label}`}
                                  data-tooltip-html={extraBadges.join("<br/>")}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} 
                                  className="inline-flex items-center rounded-full bg-[#f1f5f9] px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                                >
                                  +{extraBadges.length}
                                </span>
                                <ReactTooltip 
                                  id={`badge-tooltip-filter-${option.value || option.label}`}
                                  place="right"
                                  className="!z-[99999] !bg-gray-900/90 dark:!bg-gray-700/90 !backdrop-blur-sm !text-white !text-[10px] !px-2 !py-1 !rounded-md !shadow-lg"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </button>
                ))}

                {filteredOptions.length === 0 && (
                  <div className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                    No options found
                  </div>
                )}
              </div>
            </div>
          );

          if (usePortal && typeof document !== 'undefined') {
            return createPortal(dropdownContent, portalTarget || document.body);
          }

          return dropdownContent;
        })()}
      </div>
    </div>
  );
}
